import { db } from '$db/local/db';
import type { MacroSession, UUID, MacroTimerSnapshot } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { nowIso } from './common';

export interface CreateMacroSessionInput {
  id: UUID;
  nodeId: UUID;
  mode: 'A' | 'B';
  focusDurationMs: number;
  breakDurationMs: number;
  timerSnapshot?: MacroTimerSnapshot | null;
}

export async function createMacroSession(input: CreateMacroSessionInput): Promise<MacroSession> {
  const userId = getActiveUserId();
  const now = nowIso();
  const session: MacroSession = {
    id: input.id,
    userId,
    nodeId: input.nodeId,
    mode: input.mode,
    startedAt: now,
    focusDurationMs: input.focusDurationMs,
    readingDurationMs: 0,
    distractions: 0,
    status: 'focus_running',
    timerSnapshot: input.timerSnapshot ?? null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.macroSessions.add(session);
  return session;
}

export async function getOpenMacroSession(userId = getActiveUserId()): Promise<MacroSession | null> {
  const rows = await db.macroSessions.where('userId').equals(userId).toArray();
  return rows.find(s => !s.deletedAt && (s.status === 'focus_running' || s.status === 'focus_paused' || s.status === 'break_running' || s.status === 'break_paused' || s.status === 'pending_finish')) || null;
}

export async function updateMacroSession(id: UUID, patch: Partial<MacroSession>): Promise<void> {
  await db.macroSessions.update(id, { ...patch, updatedAt: nowIso() });
}

export async function finalizeMacroSession(id: UUID, status: MacroSession['status']): Promise<void> {
  await db.macroSessions.update(id, { status, endedAt: nowIso(), updatedAt: nowIso() });
}

export async function getMacroSession(id: UUID): Promise<MacroSession | undefined> {
  return db.macroSessions.get(id);
}

export async function softDeleteMacroSession(id: UUID): Promise<void> {
  const now = nowIso();
  
  await db.transaction('rw', [db.macroSessions, db.problemAttempts, db.conjectures], async () => {
    // 1. Borrar lógicamente la sesión
    await db.macroSessions.update(id, { deletedAt: now, updatedAt: now });

    // 2. Borrar lógicamente los intentos de problemas
    const attempts = await db.problemAttempts.where('macroSessionId').equals(id).toArray();
    for (const attempt of attempts) {
      await db.problemAttempts.update(attempt.id, { deletedAt: now, updatedAt: now });
    }

    // 3. Borrar lógicamente las conjeturas
    const conjectures = await db.conjectures.where('sessionId').equals(id).toArray();
    for (const conj of conjectures) {
      await db.conjectures.update(conj.id, { deletedAt: now, updatedAt: now });
    }
  });
}

export async function listMacroSessionsPage(options: { limit?: number } = {}): Promise<{ sessions: MacroSession[], nextCursor: string | null }> {
  const userId = getActiveUserId();
  const limit = options.limit ?? 20;
  const rows = await db.macroSessions.where('userId').equals(userId).reverse().sortBy('startedAt');
  const active = rows.filter(r => !r.deletedAt);
  return { sessions: active.slice(0, limit), nextCursor: null };
}